export default {
  domain: 'O',
  title: 'Offenheit für Erfahrungen',
  shortDescription: 'Offenheit für Erfahrungen beschreibt eine geistige Dimension, die zwischen einfallsreichen, kreativen Menschen und bodenständigen, konventionellen Menschen unterscheidet.',
  description: `Offene Menschen sind geistig neugierig, schätzen Kunst, und 
haben ein Auge für Schönheit. Sie tendieren dazu, im Gegensatz zu geschlossenen
Leuten, mehr im Einklang mit ihren Gefühlen zu sein. Sie neigen dazu, in 
ungewöhnlicher Weise zu denken oder zu handeln. Intellektuelle erzielen 
üblicherweise eine hohe Punktzahl in Offenheit für Erfahrungen; 
Infolgedessen wird dieser Faktor auch Kultur oder Intellekt genannt.
<br /> <br />
Nichtsdestotrotz wird Intellekt besser als einer der Aspekte von 
Offenheit für Erfahrungen betrachtet. Punktzahlen in Offenheit für 
Erfahrungen sind nur bedingt mit der Bildung oder Ergebnissen von 
standardisierten Intelligenztests verbunden. <br /><br />
Eine andere Charakteristik des geistig offenen Stils ist eine 
Leichtigkeit für abstraktes Denken, weit entfernt von konkreten 
Erfahrungen. Je nachdem was genau diese spezielle Fähigkeit des 
Individuums ist, kann sie in der Form vom Mathematik, logischem 
oder geometrischem Denken, kunstvoller oder metaphorischer Nutzung der 
Sprache, musikalischen Kompositionen oder Auftritten, oder eine der 
vielen visuellen oder darstellenden Künsten ausgeprägt sein.
<br /><br />
Menschen mit niedriger Punktzahl in Offenheit für Erfahrungen tendieren 
zu üblichen, knappen Interessen. Sie bevorzugen das Einfache, 
Unkomplizierte, und Offensichtliche gegenüber dem Komplexen, Mehrdeutigen, und 
Subtilen. Sie könnten Kunst und Wissenschaft gegenüber misstrauisch sein 
und die Bemühungen als abstrus und ohne praktischen Nutzen sehen. 
Verschlossene Menschen ziehen Gewohnheit Neuheiten vor; sie sind 
konservativ und resistent gegenüber Veränderungen.
<br /><br />
Offenheit wird von Psychologen oft als gesünder und erwachsener 
gehalten, wenn sie selbst offen gegenüber neuen Erfahrungen sind. 
Allerdings sind beide Denkweisen in verschiedenen Umgebungen nützlich. 
Der intellektuelle Stil der offenen Person könnte für einen 
Professoren brauchbar sein, während Forschungen zeigten, dass 
geschlossene Denkweisen zu überlegenden Arbeitsleistungen in der 
Polizeiarbeit, im Verkauf und einigen Dienstleistungsberufen führen.`,
  results: [
    {
      score: 'low',
      text: `Deine Punktzahl in Offenheit für Erfahrungen ist niedrig, 
was darauf schließen lässt, dass du gerne in schlichter und einfacher 
Weise denkst. Andere beschreiben dich als bodenständig, pragmatisch und 
konservativ.`
    },
    {
      score: 'neutral',
      text: `Deine Punktzahl in Offenheit für Erfahrungen ist 
durchschnittlich, was darauf schließen lässt, dass du gerne 
traditionell denkst, aber offen für neue Dinge bist. Deine Denkweise ist 
weder simpel noch komplex. Auf andere wirkst du wie eine gebildete 
Person, aber nicht wie ein Intellektueller.`
    },
    {
      score: 'high',
      text: `Deine Punktzahl in Offenheit für Erfahrungen ist hoch, was 
darauf schließen lässt, dass du Neuheiten, Abwechslung und Veränderungen 
magst. Du bist neugierig, einfallsreich und kreativ.`
    }
  ],
  facets: [
    {
      facet: 1,
      title: 'Einbildungskraft',
      text: `Für einfallsreiche Individuen ist die reale Welt
oft zu einfach und gewöhnlich. Menschen mit hoher Punktzahl in dieser 
Skala nutzen die Fantasie als einen Weg um eine reichere, interessantere 
Welt zu schaffen. Menschen mit niedriger Punktzahl auf diesem Gebiet 
sind mehr an Fakten als an Fantasie interessiert.`
    },
    {
      facet: 2,
      title: 'Künstlerisches Interesse',
      text: `Menschen mit hoher Punktzahl in dieser Skala lieben 
Schönheit sowohl in der Kunst als auch in der Natur. Sie vertiefen sich 
schnell und einfach in Kunst- und Naturereignisse. Sie sind oft nicht 
künstlerisch geschult, obwohl es viele später werden. Das entscheidende 
Detail dieser Skala ist das Interesse in, und Wertschätzung von 
natureller und künstlerischer Schönheit. Menschen mit niedriger 
Punktzahl fehlt die ästhetische Sensibilität und das Interesse an 
Kunst.`
    },
    {
      facet: 3,
      title: 'Emotionalität',
      text: `Personen mit hoher Emotionaliät haben eine gute Kontrolle über 
und Wahrnehmung ihrer eigenen Gefühle. Menschen mit niedriger Punktzahl 
sind sich ihrer Gefühle weniger bewustt und tendieren dazu, diese nicht 
offen zu zeigen.`
    },
    {
      facet: 4,
      title: 'Abenteuerlust',
      text: `Menschen mit hoher Punktzahl in Abenteuerlust sind gewillt 
neue Aktivitäten zu versuchen, in fremde Länder zu reisen und 
verschiedene Dinge zu erleben. Sie finden Vertrautheit und Routine 
langweilig und fahren einen neuen Weg nach Hause, nur weil er anders 
ist. Menschen mit niedriger Punktzahl neigen zu Unwohlsein bei 
Veränderungen und ziehen vertraute Routinen vor.`
    },
    {
      facet: 5,
      title: 'Intellekt',
      text: `Intellekt und künstlerisches Interesse sind die zwei 
wichtigsten, zentralen Aspekte von Offenheit für Erfahrungen. Menschen 
mit hoher Punktzahl in Intellekt lieben es mit Ideen zu spielen. Sie 
sind unvoreingenommen gegenüber neuen und ungewöhnlichen Ideen, und 
mögen es über intellektuelle Probleme zu diskutieren. Ihnen gefallen 
Rätsel, Puzzle und Denkaufgaben. Menschen mit niedriger Punktzahl 
bevorzugen es mit Menschen oder Dingen statt Ideen zu arbeiten. Sie 
sehen intellektuelle Übungen als eine Zeitverschwendung. Intellekt 
sollte nicht mit Intelligenz verwechselt werden. Intellekt ist eine 
Denkweise, keine Fähigkeit, auch wenn Menschen mit hoher Punktzahl in 
standardisierten Intelligenztests meist besser abschneiden als Menschen 
mit niedriger Punktzahl.`
    },
    {
      facet: 6,
      title: 'Liberalismus',
      text: `Psychologischer Liberalismus bezieht sich auf eine 
Bereitschaft um die Autorität, Konvention und traditionelle Werte in 
Frage zu stellen. In seiner extremsten Form zeigt psychologischer 
Liberalismus absolute Feindseligkeit gegenüber Regeln, Sympathie für 
Gesetzesbrecher, und eine Liebe für Unklarheit, Chaos und Unordnung. 
Psychologische Konservative bevorzugen die Sicherheit und Stabilität 
durch Anpassung an Traditionen. Psychologischer Liberalismus und 
Konservatismus spiegeln nicht die politische Gesinnung wieder, die 
Individuen neigen aber oft zu bestimmten politischen Parteien.`
    }
  ]
};
